import React from "react";
import {
  CloseOutlined,
  FilePdfOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Image, Spin } from "antd";
import VideoPlayerModal from "../../components/VideoPlayerModal";

const THUMB_SIZE = 56;

const getMediaKind = (fileName = "") => {
  const ext = fileName.split(".").pop()?.toLowerCase()?.split(/[?#]/)[0];
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    return "image";
  }
  if (["mp4", "3gp", "mov", "webm"].includes(ext)) {
    return "video";
  }
  if (ext === "pdf") {
    return "pdf";
  }
  return "file";
};

const MediaThumb = ({ item }) => {
  const kind = getMediaKind(item.name);

  if (kind === "image" && item.link) {
    return (
      <Image
        src={item.link}
        alt={item.name || "selected"}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
        wrapperStyle={{
          width: "100%",
          height: "100%",
        }}
        preview={true}
      />
    );
  }

  if (kind === "video" && item.link) {
    return (
      <>
        <video
          src={item.link}
          muted
          playsInline
          preload="metadata"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.25)",
            pointerEvents: "none",
          }}
        >
          <PlayCircleOutlined style={{ fontSize: 22, color: "#fff" }} />
        </div>
      </>
    );
  }

  if (kind === "pdf") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f0f0f0",
        }}
      >
        <FilePdfOutlined style={{ fontSize: 24, color: "#cf1322" }} />
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f0f0f0",
        fontSize: 11,
        padding: 4,
        textAlign: "center",
        wordBreak: "break-all",
      }}
    >
      {item.name || "File"}
    </div>
  );
};

const SelectedMediaStrip = ({
  items = [],
  onRemove,
  renderAddButton,
}) => {
  const [videoPlayer, setVideoPlayer] = React.useState({
    open: false,
    url: "",
    title: "",
  });

  if (!items.length) {
    return null;
  }

  const openVideoPlayer = (url, title) => {
    setVideoPlayer({
      open: true,
      url,
      title: title || "Video",
    });
  };

  return (
    <>
      <div
        className="wa-selected-media-strip"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px 4px",
          overflowX: "auto",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {items.map((item, index) => {
          const kind = getMediaKind(item.name);
          const isVideo = kind === "video" && !!item.link;

          return (
            <div
              key={item.clientId}
              onClick={
                isVideo
                  ? () => openVideoPlayer(item.link, item.name)
                  : undefined
              }
              style={{
                position: "relative",
                flexShrink: 0,
                width: THUMB_SIZE,
                height: THUMB_SIZE,
                borderRadius: 8,
                overflow: "hidden",
                border: index === 0 ? "2px solid #25d366" : "2px solid transparent",
                boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                cursor: isVideo ? "pointer" : undefined,
              }}
            >
              <MediaThumb item={item} />
              {item.status === "uploading" && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255, 255, 255, 0.72)",
                    pointerEvents: "none",
                  }}
                >
                  <Spin indicator={<LoadingOutlined spin style={{ fontSize: 20 }} />} />
                </div>
              )}
              <button
                type="button"
                aria-label="Remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.(item.clientId);
                }}
                style={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(0, 0, 0, 0.65)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: 10,
                  lineHeight: 1,
                  zIndex: 10,
                }}
              >
                <CloseOutlined />
              </button>
            </div>
          );
        })}
        {renderAddButton?.()}
      </div>
      <VideoPlayerModal
        open={videoPlayer.open}
        onClose={() => setVideoPlayer({ open: false, url: "", title: "" })}
        url={videoPlayer.url}
        title={videoPlayer.title}
      />
    </>
  );
};

export default SelectedMediaStrip;
